import type { BcfComment, BcfTopic } from './bcfParser'
import { mesmosCampos } from './bcfSync'

/** O que viaja entre o cliente e o servidor, e como se volta a montar.
 *
 *  No fio, os tópicos e os comentários vão separados. Não é uma
 *  arrumação arbitrária: é o que permite a duas pessoas comentarem o
 *  mesmo tópico ao mesmo tempo sem escreverem na mesma linha da base de
 *  dados, e é o que faz de um comentário uma coisa que se acrescenta em
 *  vez de uma alteração ao tópico.
 *
 *  Deste lado voltam a ser um tópico com os seus comentários, que é
 *  como a aplicação os conhece. Este ficheiro é a costura, e é puro
 *  para se poder testar sem servidor.
 */

export interface TopicoNoFio {
  guid: string
  payload: Record<string, unknown>
  apagado?: boolean
}

export interface ComentarioNoFio {
  guid: string
  topic_guid: string
  payload: Record<string, unknown>
  apagado?: boolean
}

export interface Envio {
  topicos: TopicoNoFio[]
  comentarios: ComentarioNoFio[]
}

/** Quantos registos (tópicos mais comentários) o servidor aceita num
 *  só envio. Igual a `MAXIMO_POR_ENVIO` em `sync_regras.py`. */
export const MAXIMO_POR_ENVIO = 2000

/** A marca de projecto que a beta.169 escrevia no payload do tópico.
 *  Já não se escreve (o BCF vai no pedido e o servidor guarda-o numa
 *  coluna); tira-se do que vier, para não aparecer no tópico. */
const CAMPO_PROJECTO_ANTIGO = 'bcf_projecto'

function mesmoComentario(a: BcfComment, b: BcfComment | undefined): boolean {
  return !!b && a.modifiedDate === b.modifiedDate && a.comment === b.comment
}

/**
 * Parte os tópicos para irem no fio.
 *
 * O tópico vai sem os comentários, e cada comentário vai por si. Um
 * tópico que leve os comentários dentro obrigava a reescrever a linha
 * toda por causa de uma resposta, e duas respostas ao mesmo tempo
 * apagavam-se uma à outra.
 *
 * Com `referencias` (o que o servidor já tem de cada tópico), só vai o
 * que mudou: o tópico só se os campos diferem, e só os comentários que
 * lá não estão iguais. Os comentários apagados vão como lápides
 * (`apagado: true`). Responder a um tópico não envia o tópico, por isso
 * nunca esbarra no bloqueio de quem o está a editar.
 */
export function paraEnvio(
  topicos: readonly BcfTopic[],
  referencias?: ReadonlyMap<string, BcfTopic | null>,
): Envio {
  const fora: Envio = { topicos: [], comentarios: [] }
  for (const t of topicos) {
    const ref = referencias?.get(t.guid) ?? null
    const { comments, commentsApagados, ...semComentarios } = t
    if (!ref || !mesmosCampos(t, ref)) {
      const payload: Record<string, unknown> = { ...semComentarios }
      fora.topicos.push({ guid: t.guid, payload })
    }
    const jaLa = new Map((ref?.comments ?? []).map((c) => [c.guid, c]))
    for (const c of comments) {
      if (mesmoComentario(c, jaLa.get(c.guid))) continue
      fora.comentarios.push({
        guid: c.guid,
        topic_guid: t.guid,
        payload: c as unknown as Record<string, unknown>,
      })
    }
    const lapidesLa = new Set(ref?.commentsApagados ?? [])
    for (const g of commentsApagados ?? []) {
      if (lapidesLa.has(g)) continue
      fora.comentarios.push({ guid: g, topic_guid: t.guid, payload: { guid: g }, apagado: true })
    }
  }
  return fora
}

/** Parte um envio em lotes que o servidor aceita. */
export function emLotes(envio: Envio, maximo = MAXIMO_POR_ENVIO): Envio[] {
  const registos: (
    | { tipo: 't'; r: TopicoNoFio }
    | { tipo: 'c'; r: ComentarioNoFio }
  )[] = [
    ...envio.topicos.map((r) => ({ tipo: 't' as const, r })),
    ...envio.comentarios.map((r) => ({ tipo: 'c' as const, r })),
  ]
  const lotes: Envio[] = []
  for (let i = 0; i < registos.length; i += maximo) {
    const lote: Envio = { topicos: [], comentarios: [] }
    for (const x of registos.slice(i, i + maximo)) {
      if (x.tipo === 't') lote.topicos.push(x.r)
      else lote.comentarios.push(x.r)
    }
    lotes.push(lote)
  }
  return lotes
}

/**
 * Volta a montar o que veio do servidor.
 *
 * Um comentário cujo tópico não veio nesta resposta fica de fora, e
 * não se inventa um tópico para o segurar: aconteceu porque o tópico
 * não mudou desde a última sincronização, e ele já cá está. Inventar
 * um vazio apagaria o que temos. Um comentário apagado entra como
 * lápide do tópico.
 */
export function compor(
  topicos: readonly TopicoNoFio[],
  comentarios: readonly ComentarioNoFio[],
): BcfTopic[] {
  const porGuid = new Map<string, BcfTopic>()
  for (const t of topicos) {
    if (t.apagado) continue
    const { [CAMPO_PROJECTO_ANTIGO]: _projecto, ...resto } = t.payload
    void _projecto
    const base = resto as unknown as BcfTopic
    porGuid.set(t.guid, { ...base, guid: t.guid, comments: [] })
  }
  for (const c of comentarios) {
    const t = porGuid.get(c.topic_guid)
    if (!t) continue
    if (c.apagado) {
      t.commentsApagados = [...new Set([...(t.commentsApagados ?? []), c.guid])]
      continue
    }
    t.comments.push(c.payload as unknown as BcfComment)
  }
  for (const t of porGuid.values()) {
    t.comments.sort((a, b) => {
      const da = a.date ?? ''
      const db = b.date ?? ''
      if (da === db) return a.guid < b.guid ? -1 : 1
      return da < db ? -1 : 1
    })
  }
  return [...porGuid.values()]
}

/** Os GUID dos tópicos que vieram marcados como apagados. */
export function apagadosEm(topicos: readonly TopicoNoFio[]): string[] {
  return topicos.filter((t) => t.apagado).map((t) => t.guid)
}

/**
 * Os comentários que chegaram para tópicos que não vieram na resposta.
 *
 * Não é um erro: o tópico não mudou, por isso o servidor não o mandou.
 * Mas o comentário tem de ir parar ao tópico que já cá está, e quem
 * chama precisa de saber quais são.
 */
export function comentariosOrfaos(
  topicos: readonly TopicoNoFio[],
  comentarios: readonly ComentarioNoFio[],
): Map<string, BcfComment[]> {
  const vieram = new Set(topicos.map((t) => t.guid))
  const fora = new Map<string, BcfComment[]>()
  for (const c of comentarios) {
    if (c.apagado || vieram.has(c.topic_guid)) continue
    const lista = fora.get(c.topic_guid)
    const comentario = c.payload as unknown as BcfComment
    if (lista) lista.push(comentario)
    else fora.set(c.topic_guid, [comentario])
  }
  return fora
}

/** Como `comentariosOrfaos`, para as lápides: os GUID dos comentários
 *  apagados lá fora em tópicos que não vieram na resposta. */
export function apagadosOrfaos(
  topicos: readonly TopicoNoFio[],
  comentarios: readonly ComentarioNoFio[],
): Map<string, string[]> {
  const vieram = new Set(topicos.map((t) => t.guid))
  const fora = new Map<string, string[]>()
  for (const c of comentarios) {
    if (!c.apagado || vieram.has(c.topic_guid)) continue
    const lista = fora.get(c.topic_guid)
    if (lista) lista.push(c.guid)
    else fora.set(c.topic_guid, [c.guid])
  }
  return fora
}
